/**
 * Phase 6.3 — chunk batching via `THREE.BatchedMesh`.
 *
 * One BatchedMesh per world holds all visible section geometries. Draw
 * calls drop from O(visible-sections) (~1500 at RD=8) to ~O(1) per frame.
 *
 * Gated behind URL flag `?batchedChunks=1` (or `localStorage.batchedChunks
 * === '1'`) during rollout. When disabled, the renderer falls back to the
 * legacy per-section Mesh path in `worldrendererThree.ts`.
 *
 * Sizing notes (see docs/phase-6.3-batched-chunks-plan.md 2026-05-25
 * addendum for memory math):
 *   maxGeoms   6144  enough for RD=8 + worldHeight 384 + headroom
 *   maxVerts   8M    ~96 MB Float32 positions
 *   maxIndices 24M   ~96 MB Uint32 indices
 *
 * On low-memory devices (RD < 6) the slots are still allocated lazily by
 * BatchedMesh — only used slots cost real GPU memory.
 *
 * What BatchedMesh CANNOT do for us:
 *   - Sign / banner / head overlays (textured Object3D children). Those
 *     stay on per-section `THREE.Group`s in `sectionOverlayObjects`.
 *   - Different materials per instance (alpha-blend vs alpha-test). Our
 *     chunk material handles both via alpha-test on the atlas, so single-
 *     material works.
 *   - Per-instance attribute overrides (e.g. one chunk has tileBounds and
 *     another doesn't). All sections in a BatchedMesh must share the
 *     attribute layout. We handle this by always emitting tileBounds in
 *     the mesher (Phase 6.1 greedy path) — when greedy is off, all-zero
 *     tileBounds get emitted and the shader uses the legacy uv path.
 */

import * as THREE from 'three'

export type ChunkBatchAttrSpec = {
  position: 'Float32'
  normal: 'Int8' | 'Float32'
  color: 'Uint8' | 'Float32'
  uv: 'Uint16' | 'Float32'
  tileBounds?: 'Float32' | 'Uint16'
  /** Index type used by the mesher (matches BatchedMesh's shared index buffer). */
  indices: 'Uint16' | 'Uint32'
}

export const chunkBatchEnabled = (): boolean => {
  try {
    if (typeof location !== 'undefined' && new URLSearchParams(location.search).get('batchedChunks') === '1') return true
    if (typeof localStorage !== 'undefined' && localStorage.batchedChunks === '1') return true
  } catch { /* ssr */ }
  return false
}

export interface ChunkBatchOptions {
  material: THREE.Material
  maxGeoms?: number
  maxVerts?: number
  maxIndices?: number
}

/** Wrapper around `THREE.BatchedMesh` providing add/update/remove keyed by section. */
export class ChunkBatch {
  readonly mesh: THREE.BatchedMesh
  /** map key → { geomId, instanceId } — r170 splits geometry vs instance. */
  private readonly idForKey = new Map<string, { geomId: number; instanceId: number }>()
  private disposed = false

  constructor (opts: ChunkBatchOptions) {
    const maxGeoms = opts.maxGeoms ?? 6144
    const maxVerts = opts.maxVerts ?? 8_000_000
    const maxIndices = opts.maxIndices ?? 24_000_000

    // THREE.BatchedMesh (r158+). Constructor: (maxInstanceCount, maxVertexCount, maxIndexCount, material).
    // Uint32 indices fine here.
    // CLONE the material. Three.js BatchedMesh patches the material's
    // defines / onBeforeCompile to inject per-instance matrix uniforms;
    // if we share the same material instance with the legacy per-section
    // Mesh path, the defines bleed and silently break one or both.
    const batchMaterial = opts.material.clone() as THREE.MeshBasicMaterial
    // Force shader recompile so any auto-injected batching defines take effect.
    batchMaterial.needsUpdate = true
    // Drop the transparent flag: our atlas uses alphaTest only (cutout),
    // not real alpha-blend. transparent=true puts the mesh in a sorted
    // transparent pass that BatchedMesh r170 may not handle correctly
    // (chunks invisible despite slots populated). alphaTest alone gives
    // identical visual output for >99% of blocks (glass/leaves/etc).
    batchMaterial.transparent = false
    this.mesh = new THREE.BatchedMesh(maxGeoms, maxVerts, maxIndices, batchMaterial)
    // Per-object frustum cull ON. While debugging the invisible-chunks
    // bug we set this false; with the addInstance fix in place,
    // BatchedMesh's per-instance cull does its job and saves GPU time
    // when chunks behind the camera would otherwise be drawn.
    ;(this.mesh as any).perObjectFrustumCulled = true
    // Outer-mesh frustum cull off — the outer bounding sphere starts
    // empty and is fragile across re-adds; per-instance cull does the
    // real work.
    this.mesh.frustumCulled = false
    this.mesh.sortObjects = false
    this.mesh.name = 'chunk-batch'
  }

  /** Add or replace the section at `key`. Returns the BatchedMesh instanceId. */
  setSection (key: string, geom: THREE.BufferGeometry, position: THREE.Vector3): number {
    if (this.disposed) throw new Error('ChunkBatch disposed')
    // BatchedMesh's per-instance frustum cull reads geometry.boundingBox /
    // boundingSphere; if absent the instance is silently invisible.
    if (!geom.boundingBox) geom.computeBoundingBox()
    if (!geom.boundingSphere) geom.computeBoundingSphere()
    let pair = this.idForKey.get(key)
    if (pair === undefined) {
      // r170+ split API: addGeometry returns geometryId; addInstance(geomId)
      // returns instanceId. Without addInstance, no renderable instance
      // exists and the batch's drawRanges/_visibility/_active stay empty.
      const geomId = this.mesh.addGeometry(geom)
      const instanceId = this.mesh.addInstance(geomId)
      this.mesh.setVisibleAt(instanceId, true)
      pair = { geomId, instanceId }
      this.idForKey.set(key, pair)
      if (this.idForKey.size === 1) {
        console.log('[chunkBatch] first geom+instance registered — attrs:',
          Object.keys(geom.attributes).map(k => {
            const a = geom.attributes[k] as THREE.BufferAttribute
            return `${k}:${(a.array as any).constructor.name}x${a.itemSize}`
          }).join(','),
          'indices:', geom.index ? `${(geom.index.array as any).constructor.name}x${geom.index.array.length}` : 'none',
          'verts:', geom.attributes.position?.count,
          'geomId=', geomId, 'instanceId=', instanceId)
      }
    } else {
      // Replace geometry contents in-place — keeps both ids, no re-alloc.
      this.mesh.setGeometryAt(pair.geomId, geom)
    }
    const m = new THREE.Matrix4().makeTranslation(position.x, position.y, position.z)
    this.mesh.setMatrixAt(pair.instanceId, m)
    return pair.instanceId
  }

  removeSection (key: string): boolean {
    const pair = this.idForKey.get(key)
    if (pair === undefined) return false
    this.mesh.deleteInstance(pair.instanceId)
    this.mesh.deleteGeometry(pair.geomId)
    this.idForKey.delete(key)
    return true
  }

  hasSection (key: string): boolean {
    return this.idForKey.has(key)
  }

  get sectionCount (): number {
    return this.idForKey.size
  }

  dispose () {
    this.disposed = true
    this.idForKey.clear()
    this.mesh.dispose()
  }
}

// Diagnostic accessor for the console. After the user enables
// ?batchedChunks=1 they can run `__chunkBatchInfo()` to confirm the
// batch is in use, see slot count, and inspect the BatchedMesh.
if (typeof window !== 'undefined') {
  ;(window as any).__chunkBatchInfo = () => {
    const w = window as any
    const renderer = w.appViewer?.backend
    const wr = renderer?.worldRenderer ?? w.viewer?.world ?? w.world
    const batch = wr?.chunkBatch as ChunkBatch | undefined
    const r = w.appViewer?.backend?.renderer ?? w.viewer?.renderer ?? null
    const info = r?.info?.render ?? {}
    if (!batch) return { enabled: chunkBatchEnabled(), batch: null, renderCalls: info.calls, triangles: info.triangles }
    const bm = batch.mesh as any
    // Use official r170 public API where possible.
    const instanceCountFn = typeof bm.getInstanceCount === 'function' ? bm.getInstanceCount.bind(bm) : null
    const geomCountFn = typeof bm.getGeometryCount === 'function' ? bm.getGeometryCount.bind(bm) : null
    return {
      enabled: chunkBatchEnabled(),
      sections: batch.sectionCount,
      maxInstanceCount: bm.maxInstanceCount,
      maxVerts: bm.maxVertexCount,
      maxIndices: bm.maxIndexCount,
      instanceCount: instanceCountFn?.() ?? '?',
      geometryCount: geomCountFn?.() ?? '?',
      visible: bm.visible,
      frustumCulled: bm.frustumCulled,
      perObjectFrustumCulled: bm.perObjectFrustumCulled,
      inScene: !!(bm.parent),
      geomAttrs: bm.geometry ? Object.keys(bm.geometry.attributes) : null,
      matMapName: (bm.material as any)?.map?.name ?? (bm.material as any)?.map?.uuid?.slice(0, 8),
      renderCalls: info.calls,
      triangles: info.triangles,
    }
  }
}
